import { type, objMatch, deepCopy, isNumber } from '../utils/assist'

// 内部使用的唯一id
var uid = 0

// 数据跟踪对象
function Track({ row, key, status, exclude }) {
	// 行数据
	this.node = row
	this.row = row._isTreeNode ? row.node : row
	this.exclude = exclude

	// 排除私有属性、id属性、rowId不存在的场景
	if (!key || !this.row.id || key.indexOf('_') == 0 || key == "id" || exclude.indexOf(key) >= 0) {
		return this
	}

	// 行字段
	this.key = key
	this.rowId = this.row.id
	this.originalValue = this.value = this.oldValue = this.row[key]
	// default - 静止状态， updated - 更新， added - 新增， deleted - 删除， newDeleted - 新增后删除
	this.status = status || "default"
}

// 获取追踪字段
Track.keys = function(row) {
	row = row._isTreeNode ? row.node : row

	return Object.keys(row)
}

// 获取rowId
Track.id = function(row) {
	row = row._isTreeNode ? row.node : row

	return row.id
}

// 比较value是否改变
Track.eq = function(a, b) {
	// getTrackValue属于对外开放接口，可以让用户自定义value
	if (a && a.getTrackValue) {
		a = a.getTrackValue()
	}

	if (b && b.getTrackValue) {
		b.getTrackValue()
	}

	// 绝大多数场景下，尽可能的执行数值比较是符合需求的
	// 字符串小数位补零是个例外，不过一般这种场景下，value的确代表数字，补零前后应该是相等的
	if (isNumber(a)) {
		a = +a
	}

	if (isNumber(b)) {
		b = +b
	}

	return a == b
}

Track.notEq = function(a, b) {
	return !Track.eq(a, b)
}

// 状态更新
Track.prototype.update = function(value) {
	// value发生改变
	if (Track.notEq(this.value, value)) {
		this.oldValue = this.value
		this.value = value
	}

	// 原始值发生改变
	if (Track.notEq(this.value, this.originalValue)) {
		// 更新
		if (this.status == "default") {
			return this.status = "updated"
		}

		// 删除后又更新
		if (this.status == "deleted") {
			return this.status = "updated"
		}

		// 新增删除后又更新
		if (this.status == "newDeleted") {
			return this.status = "added"
		}
	} else {
		// 更新后还原
		if (this.status == "updated") {
			return this.status = "default"
		}

		// 删除后还原
		if (this.status == "deleted") {
			return this.status = "default"
		}

		// 新增删除后又还原
		if (this.status == "newDeleted") {
			return this.status = "added"
		}
	}
}

// 删除
Track.prototype.delete = function() {
	if (this.status == "added") {
		this.status = "newDeleted"
	} else {
		this.status = "deleted"
	}
}

// 导出数据
Track.prototype.copyData = function() {
	var options = {
		exclude: this.exclude,
		depth: 1
	}

	var node = deepCopy(this.node, options),
		row = deepCopy(this.row, options)

	return {
		node,
		row,
		key: this.key,
		rowId: this.rowId,
		originalValue: this.originalValue,
		value: this.value,
		oldValue: this.oldValue,
		status: this.status
	}
}

export default {
	props: {
		// 需要排除追踪的字段
		trackExclude: {
			type: Array,
			default: () => []
		}
	},
	data() {
		return {
			// track
			_trackMap: {},
			_trackList: [],
		}
	},

	computed: {
		// 获取行数据集合
		flatData() {
			return this.data
		},
	},

	methods: {
		// 初始化跟踪状态，created阶段会默认调用一次，异步取数场景需要自行调用
		initTrack() {
			var keys

			this._trackMap = {}
			this._trackList = []

			this.flatData.forEach(row => {
				// 从行数据中获取key，columns中的key只是它们的子集
				if (!keys) {
					keys = Track.keys(row)
				}

				keys.forEach(key => this.addTrack(row, key))
			})
		},

		// 添加跟踪对象
		addTrack(row, key, status) {
			var track = new Track({ row, key, status, exclude: this.trackExclude })

			// 无法追踪rowId不存在的行对象
			if (!track.rowId) {
				return
			}

			if (!this._trackMap[track.rowId]) {
				this.$set(this._trackMap, track.rowId, {})
			}

			this.$set(this._trackMap[track.rowId], track.key, track)
			this._trackList.push(track)
		},

		// 修改/新增track
		saveTrack(row, key) {
			if (row) {
				var keys = key ? [key] : Track.keys(row)

				keys.forEach(k => {
					var track = this._trackMap[Track.id(row)]

					track = track && track[k]

					if (track) {
						track.update(row[k])
					} else {
						this.addTrack(row, k, "added")
					}
				})
			}
		},

		// 删除跟踪
		deleteTrack(row, key) {
			if (row) {
				var keys = key ? [key] : Track.keys(row)

				keys.forEach(k => {
					var track = this._trackMap[Track.id(row)]

					track = track && track[k]

					if (track) {
						track.delete()
					}
				})
			}
		},

		// 寻找新增的行
		// todo tree-table 暂不支持新增、删减行，即使要做，也只能在tree-table-node上监听以及操作
		seekNewRows(newList, oldList) {
			var news = []

			var oldIdMap = oldList.reduce((memo, row) => {
				memo[row.id] = true

				return memo
			}, {})

			newList.forEach(row => {
				// 为新增行赋予id
				if (!row.id) {
					row.id = '_trackId' + (uid++)
				}

				if (!oldIdMap[row.id]) {
					news.push(row)
				}
			})

			return news
		},

		// 寻找删除的行
		seekDeletedRows(newList, oldList) {
			var deleteds = []

			var newIdMap = newList.reduce((memo, row) => {
				memo[row.id] = true

				return memo
			}, {})

			oldList.forEach(row => {
				if (!newIdMap[row.id]) {
					deleteds.push(row)
				}
			})

			return deleteds
		},

		// 获取单元格数据
		getTrackCellData(predicate) {
			if (type(predicate) == "object") {
				return this._trackList.filter(v => objMatch(v, predicate)).map(v => v.copyData())
			}

			if (type(predicate) == "function") {
				return this._trackList.filter(predicate).map(v => v.copyData())
			}
		},

		// 获取行数据
		getTrackRowData(predicate) {
			var map = {}

			if (type(predicate) == "object") {
				this._trackList.filter(v => objMatch(v, predicate))
					.forEach(track => {
						if (!map[track.rowId]) {
							map[track.rowId] = deepCopy(track.row, {
								depth: 1
							})
						}
					})
			}

			if (type(predicate) == "function") {
				this._trackList.filter(predicate)
					.forEach(track => {
						if (!map[track.rowId]) {
							map[track.rowId] = deepCopy(track.row, {
								depth: 1
							})
						}
					})
			}

			return Object.values(map)
		},
	}
}